import { Box, Typography } from "@mui/material";
import InterestEmptyState from "./empty-state";
import InterestCard from "./card";
import { useInterestsInfiniteQuery } from "../../hooks/interests/interests.hook";
import LoadingSkeleton from "@/modules/common/components/ui/loadingSkeleton";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import AddInterest from "./add-interest";
import { useContext, useEffect, useRef } from "react";

export default function InterestsTab() {
  const { lead } = useContext(LeadPageContext);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInterestsInfiniteQuery({
      journeyUids: [lead?.data.uid ?? ""],
    });

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "50px", threshold: 0.1 }
    );

    const node = loadMoreRef.current;
    observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <LoadingSkeleton rows={5} heights={["50px"]} />;

  const total = data?.pages?.[0]?.data?.total ?? 0;
  const allResults = data?.pages?.flatMap((p) => p.data.results) ?? [];

  if (!total) return <InterestEmptyState />;

  return (
    <Box sx={{ height: "90%" }}>
      {/* Header */}
      <Box
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.primary,
          p: t.nawy.spacing.md,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        })}
      >
        <Box
          sx={(t) => ({
            display: "flex",
            gap: t.nawy.spacing.xxs,
            alignItems: "baseline",
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.headline.sm}
          >
            Interests
          </Typography>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
          >
            ({total})
          </Typography>
        </Box>
        <AddInterest />
      </Box>

      {/* Scrollable list */}
      <Box
        sx={(t) => ({
          p: t.nawy.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
          height: "100%",
          overflowY: "auto",
        })}
      >
        {allResults.map((i) => (
          <InterestCard key={i.uid} interest={i} />
        ))}

        {/* Loader trigger element */}
        <div ref={loadMoreRef} />

        {/* Loading indicator while fetching next page */}
        {isFetchingNextPage && <LoadingSkeleton heights={["50px"]} />}
      </Box>
    </Box>
  );
}
